import React, { useCallback } from 'react';
import { FG141 } from '../../../services/g204Api';
import { fmt } from '../helpers';

interface Tab1DetailGridProps {
  details: Partial<FG141>[];
  selectedRow: number;
  isEditing: boolean;
  setSelectedRow: (idx: number) => void;
  setDetails: React.Dispatch<React.SetStateAction<Partial<FG141>[]>>;
  addDetailRow: () => void;
  deleteDetailRow: () => void;
}

const Tab1DetailGrid: React.FC<Tab1DetailGridProps> = React.memo(({
  details, selectedRow, isEditing,
  setSelectedRow, setDetails,
  addDetailRow, deleteDetailRow,
}) => {
  /** Update a detail field inline */
  const updateDetailField = useCallback((rowIdx: number, key: keyof FG141, value: FG141[keyof FG141]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[rowIdx]) {
        const updated = { ...next[rowIdx], [key]: value };
        // BR-04: Auto-calculate tax and total when fg141_13 (net amount) changes
        if (key === 'fg141_13') {
          const net = parseFloat(String(value)) || 0;
          const tax = Math.round(net * 0.17);
          updated.fg141_14 = tax;
          updated.fg141_17 = net + tax;
        }
        // If tax (fg141_14) is manually changed, recalc total
        if (key === 'fg141_14') {
          const net = updated.fg141_13 || 0;
          const tax = parseFloat(String(value)) || 0;
          updated.fg141_17 = net + tax;
        }
        next[rowIdx] = updated;
      }
      return next;
    });
  }, [setDetails]);

  return (
    <div className="table-wrapper">
      <div className="table-toolbar">
        <div className="left">
          <span style={{ fontSize: 13, fontWeight: 500 }}>折讓明細 (FG141)</span>
        </div>
        <div className="right">
          <button
            className="btn btn-sm"
            disabled={!isEditing || details.length >= 99}
            onClick={addDetailRow}
          >
            明細新增
          </button>
          <button
            className="btn btn-sm btn-danger"
            disabled={!isEditing || details.length === 0}
            onClick={deleteDetailRow}
          >
            明細刪除
          </button>
        </div>
      </div>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 50 }} data-contract-field="fg141_02">項次</th>
              <th data-contract-field="fg141_11">交運單號</th>
              <th data-contract-field="fg141_12">公司代號</th>
              <th className="num" data-contract-field="fg141_13">折讓淨額</th>
              <th className="num" data-contract-field="fg141_14">稅額</th>
              <th data-contract-field="fg141_16">幣別</th>
              <th className="num" data-contract-field="fg141_17">折讓金額</th>
              <th data-contract-field="fg141_18">客訴單號</th>
            </tr>
          </thead>
          <tbody>
            {details.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', color: '#999', padding: 20 }}>
                  無明細資料
                </td>
              </tr>
            )}
            {details.map((d, i) => (
              <tr
                key={i}
                className={selectedRow === i ? 'selected' : ''}
                onClick={() => setSelectedRow(i)}
              >
                <td>{d.fg141_02 || i + 1}</td>
                <td>
                  <input
                    type="text"
                    value={d.fg141_11 || ''}
                    onChange={e => updateDetailField(i, 'fg141_11', e.target.value)}
                    disabled={!isEditing}
                    style={{ width: 100 }}
                  />
                </td>
                <td>{d.fg141_12 || ''}</td>
                <td className="num">
                  <input
                    type="number"
                    value={d.fg141_13 ?? ''}
                    onChange={e => updateDetailField(i, 'fg141_13', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                    style={{ width: 120, textAlign: 'right' }}
                  />
                </td>
                <td className="num">
                  <input
                    type="number"
                    value={d.fg141_14 ?? ''}
                    onChange={e => updateDetailField(i, 'fg141_14', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                    style={{ width: 100, textAlign: 'right' }}
                  />
                </td>
                <td>{d.fg141_16 || ''}</td>
                <td className="num">{fmt(d.fg141_17)}</td>
                <td>
                  <input
                    type="text"
                    value={d.fg141_18 || ''}
                    onChange={e => updateDetailField(i, 'fg141_18', e.target.value)}
                    disabled={!isEditing}
                    style={{ width: 100 }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

Tab1DetailGrid.displayName = 'Tab1DetailGrid';
export default Tab1DetailGrid;
